import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import ComuneCombobox from "../components/ComuneCombobox";

export default function Auth() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");
  const [loading, setLoading] = useState(false);

  const [lEmail, setLEmail] = useState("");
  const [lPass, setLPass] = useState("");

  const [rName, setRName] = useState("");
  const [rEmail, setREmail] = useState("");
  const [rPass, setRPass] = useState("");
  const [rComune, setRComune] = useState("Fiano Romano");
  const [rPhone, setRPhone] = useState("");

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(lEmail, lPass);
      toast.success("Bentornato!");
      navigate("/area-personale");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register({ name: rName, email: rEmail, password: rPass, comune: rComune, phone: rPhone });
      toast.success("Registrazione completata!");
      navigate("/area-personale");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1518457900604-5c973dffdedf?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-primary/40" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <BookOpen className="w-8 h-8 mb-4" />
          <p className="font-serif text-3xl font-semibold leading-tight">Ogni libro merita<br />un nuovo lettore.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <h1 className="text-3xl font-serif font-bold tracking-tight mb-1">Benvenuto</h1>
          <p className="text-muted-foreground text-sm mb-8">Accedi o crea un account per scambiare libri.</p>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 w-full mb-6">
              <TabsTrigger value="login" data-testid="tab-login">Accedi</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Registrati</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={doLogin} className="space-y-4">
                <div>
                  <Label htmlFor="le">Email</Label>
                  <Input id="le" data-testid="login-email" type="email" required value={lEmail} onChange={(e) => setLEmail(e.target.value)} className="mt-1.5 h-11" />
                </div>
                <div>
                  <Label htmlFor="lp">Password</Label>
                  <Input id="lp" data-testid="login-password" type="password" required value={lPass} onChange={(e) => setLPass(e.target.value)} className="mt-1.5 h-11" />
                </div>
                <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full h-11 bg-primary hover:bg-primary/90">{loading ? "Attendere…" : "Accedi"}</Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={doRegister} className="space-y-4">
                <div>
                  <Label htmlFor="rn">Nome pubblico</Label>
                  <Input id="rn" data-testid="register-name" required value={rName} onChange={(e) => setRName(e.target.value)} placeholder="Es. Maria R." className="mt-1.5 h-11" />
                </div>
                <div>
                  <Label htmlFor="re">Email</Label>
                  <Input id="re" data-testid="register-email" type="email" required value={rEmail} onChange={(e) => setREmail(e.target.value)} className="mt-1.5 h-11" />
                </div>
                <div>
                  <Label htmlFor="rp">Password</Label>
                  <Input id="rp" data-testid="register-password" type="password" required minLength={6} value={rPass} onChange={(e) => setRPass(e.target.value)} placeholder="Almeno 6 caratteri" className="mt-1.5 h-11" />
                </div>
                <div>
                  <Label>Comune / Zona</Label>
                  <ComuneCombobox value={rComune} onChange={setRComune} testId="register-comune" allowAltro placeholder="Cerca comune…" className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="rph">Telefono <span className="text-muted-foreground font-normal">(facoltativo, privato)</span></Label>
                  <Input id="rph" data-testid="register-phone" value={rPhone} onChange={(e) => setRPhone(e.target.value)} placeholder="Visibile solo dopo un accordo" className="mt-1.5 h-11" />
                  <p className="text-xs text-muted-foreground mt-1.5">L'indirizzo esatto e i recapiti non compaiono mai nel catalogo pubblico.</p>
                </div>
                <Button data-testid="register-submit" type="submit" disabled={loading} className="w-full h-11 bg-primary hover:bg-primary/90">{loading ? "Attendere…" : "Crea account"}</Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
